"use client";

import type { UIMessage } from "ai";

/** A tool the assistant invoked mid-turn (e.g. rag_search), surfaced so the UI
 * shows activity ("Searching the knowledge base…") instead of a blank turn. */
export interface ToolActivity {
  toolCallId: string;
  /** Raw tool name from the stream, e.g. "rag_search". */
  toolName: string;
  state: "running" | "done" | "error";
  /** Arguments the model passed, once the stream settles them (input-available). */
  input?: unknown;
  /** What the tool returned (output-available). */
  output?: unknown;
  /** Failure detail (output-error). */
  errorText?: string;
}

/** A retrieved source the assistant cited (from rag_search). */
export interface SourceRef {
  id: string;
  title: string;
  /** "pp. 12–14"-style locator. */
  detail?: string;
  /** The retrieved passage, shown when the citation is opened. */
  text?: string;
  /** Cosine similarity 0–1, if known. */
  score?: number;
}

/** A host extension part carried by a `UIMessage` as an AI SDK "data-*" part
 * (`type: "data-<renderAs>"`) — the live-stream/reload-hydration counterpart
 * to `Turn`'s own `{ kind: "opaque" }` part (MIN-297/MIN-293). Shipped
 * byte-for-byte; this layer never interprets `data`. */
export interface OpaqueRef {
  renderAs: string;
  data: unknown;
}

/** A normalized chat turn derived from a Vercel AI SDK UIMessage. */
export interface ChatBubble {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** Assistant only: tools invoked during the turn, in call order. */
  tools: ToolActivity[];
  /** Assistant only: sources cited during the turn. */
  sources: SourceRef[];
  /** Assistant only: streamed model thought summary (Gemini thinking), if any. */
  reasoning: string;
  /** Assistant only: host-extension ("data-*") parts, in stream order. Absent
   * from every bubble until a host actually emits one — see `turnToUiMessage`. */
  opaque?: OpaqueRef[];
  /** Assistant only: true when the run was cut short (client disconnect, or the
   * clinician hitting stop) — `text` is a partial reply, not a complete answer. */
  interrupted?: boolean;
}

/** Flatten AI SDK v5 UIMessages into render-ready bubbles: text, reasoning,
 * tool activity, and cited sources. Sources carry their passage + score in
 * `providerMetadata.mindforge` (emitted by the ai-engine chat controller). */
export function uiToBubbles(messages: UIMessage[]): ChatBubble[] {
  return messages.map((m) => {
    let text = "";
    let reasoning = "";
    const tools: ToolActivity[] = [];
    const sources: SourceRef[] = [];
    const opaque: OpaqueRef[] = [];
    for (const p of m.parts) {
      const type = p.type as string;
      if (type === "text") {
        text += (p as { text: string }).text;
      } else if (type === "reasoning") {
        reasoning += (p as { text: string }).text;
      } else if (type === "dynamic-tool" || type.startsWith("tool-")) {
        // A tool call ends the current text run: whatever the model wrote
        // before deciding to call a tool was narration toward the answer, not
        // the answer itself. Gemini narrates freely and then restates the same
        // facts once the tool returns — concatenating both rendered the answer
        // twice. The server persists `result.output` (the final run alone), so
        // keeping only the last run is also what makes the live view match what
        // a reload shows.
        text = "";
        // state goes input-streaming/input-available -> output-available|error.
        const tp = p as {
          toolCallId: string;
          toolName?: string;
          state?: string;
          input?: unknown;
          output?: unknown;
          errorText?: string;
        };
        const toolName = type === "dynamic-tool" ? (tp.toolName ?? "tool") : type.slice("tool-".length);
        const state = tp.state === "output-available" ? "done" : tp.state === "output-error" ? "error" : "running";
        // input/output/errorText ride the same part as `state`; each is absent
        // until its stage, so spread them conditionally rather than writing
        // `undefined` keys the row would then have to re-check.
        tools.push({
          toolCallId: tp.toolCallId,
          toolName,
          state,
          ...(tp.input !== undefined ? { input: tp.input } : {}),
          ...(tp.output !== undefined ? { output: tp.output } : {}),
          ...(tp.errorText !== undefined ? { errorText: tp.errorText } : {}),
        });
      } else if (type === "source-document" || type === "source-url") {
        const sp = p as {
          sourceId: string;
          title?: string;
          url?: string;
          filename?: string;
          providerMetadata?: { mindforge?: { text?: string; score?: number } };
        };
        const mf = sp.providerMetadata?.mindforge;
        sources.push({
          id: sp.sourceId,
          title: sp.title ?? sp.url ?? sp.filename ?? "source",
          detail: type === "source-document" ? sp.filename : sp.url,
          text: mf?.text,
          score: typeof mf?.score === "number" ? mf.score : undefined,
        });
      } else if (type.startsWith("data-")) {
        // Host extension part (`turnToUiMessage`'s opaque encoding) — carried
        // through unmodified for `Thread`'s Turn bridge to reach the registry.
        const dp = p as { data: unknown };
        opaque.push({ renderAs: type.slice("data-".length), data: dp.data });
      }
    }
    const metadata = m.metadata as { interrupted?: boolean } | undefined;
    return {
      id: m.id,
      role: m.role === "user" ? "user" : "assistant",
      text,
      tools,
      sources,
      reasoning,
      ...(opaque.length > 0 ? { opaque } : {}),
      ...(metadata?.interrupted ? { interrupted: true } : {}),
    };
  });
}
